import { BackendEnvironmentEnum, CookieEnum, EventNameEnum } from './types/enums';
import { mapButtonName } from './mapButtonName';
import { v4 as uuidV4 } from 'uuid';
import { mapContext } from './mapContext';
import { removeUndefinedValues } from './removeUndefinedValues';
import { sendWithBeacon } from './sendWithBeacon';
import { mapDestinations } from './mapDestinations';
import { DEFAULT_DESTINATIONS } from './types/constants';
import { mergeCustomData } from './mergeCustomData';
import { getCookie } from './getCookie';
import { fetchMissingCookies } from './fetchMissingCookies';
var sendEvent = function (event, getInitParams, options) {
    var _a;
    // Retrieve the params when sending the event rather that when initializing to ensure that all cookies are set
    var initParams = getInitParams();
    var trackingApiUrl = buildTrackingApiUrl(initParams);
    var destinations = mapDestinations(initParams, options, event.name);
    if (destinations.length !== 0 || event.name === EventNameEnum.ConsentChanged) {
        var trackingEvent = {
            anonymousId: (_a = initParams.user.anonymousId) !== null && _a !== void 0 ? _a : '',
            bank: initParams.bank,
            context: mapContext(initParams, window, options),
            destinations: event.name === EventNameEnum.ConsentChanged ? DEFAULT_DESTINATIONS : destinations,
            event: event.name,
            messageId: uuidV4(),
            timestamp: new Date().toISOString(),
            type: event.name === EventNameEnum.PageLoaded ? 'page' : 'track',
            properties: removeUndefinedValues(event.properties),
            customData: mergeCustomData(initParams.customData, options === null || options === void 0 ? void 0 : options.customData),
        };
        sendWithBeacon(trackingApiUrl)(trackingEvent);
        // For debugging purposes. Request payload is lost when navigating to another page.
        var printSignal = getCookie(CookieEnum.PrintSignal);
        if (printSignal == 'true') {
            console.log(JSON.stringify(trackingEvent, null, 2));
        }
    }
};
var buildTrackingApiUrl = function (initParams) {
    switch (initParams.backendEnvironment) {
        case BackendEnvironmentEnum.Test:
            return 'https://www.test.sparebank1.no/api/tracking/webbehavior';
        case BackendEnvironmentEnum.Prod:
            return 'https://www.sparebank1.no/api/tracking/webbehavior';
        default:
            return '/api/tracking/webbehavior';
    }
};
/**
 * Initializes the tracker, details are documented in the [Readme](../pages/README.html)
 *
 * @category WebBehaviorTracker
 */
export var initWebBehaviorTracker = function (getInitParams) {
    if (!getInitParams().disableTrackingCookies) {
        fetchMissingCookies(getInitParams());
    }
    return {
        page: function (options, properties) {
            return sendEvent({
                name: EventNameEnum.PageLoaded,
                properties: {
                    internalPromotionName: properties === null || properties === void 0 ? void 0 : properties.internalPromotionName,
                    personalization: properties === null || properties === void 0 ? void 0 : properties.personalization,
                    discountName: properties === null || properties === void 0 ? void 0 : properties.discountName,
                    customerStatus: properties === null || properties === void 0 ? void 0 : properties.customerStatus,
                    pageTag: properties === null || properties === void 0 ? void 0 : properties.pageTag,
                },
            }, getInitParams, options);
        },
        consentChanged: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.ConsentChanged,
                properties: {
                    consentName: properties.consentName,
                    previousFunctional: properties.previousFunctional,
                    previousStatistic: properties.previousStatistic,
                    previousMarketing: properties.previousMarketing,
                    updatedFunctional: properties.updatedFunctional,
                    updatedStatistic: properties.updatedStatistic,
                    updatedMarketing: properties.updatedMarketing,
                },
            }, getInitParams, options);
        },
        buttonClicked: function (properties, options) {
            var _a;
            return sendEvent({
                name: EventNameEnum.ButtonClicked,
                properties: {
                    buttonId: properties.id,
                    buttonText: properties.text,
                    buttonName: mapButtonName(properties),
                    linkType: (_a = properties.linkType) !== null && _a !== void 0 ? _a : 'button',
                    linkUrl: properties.linkUrl,
                    closestAncestor: properties.closestAncestor,
                },
            }, getInitParams, options);
        },
        formInitiated: function (properties, options) {
            var _a;
            return sendEvent({
                name: EventNameEnum.FormInitiated,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: (_a = properties.formStepName) !== null && _a !== void 0 ? _a : 'initiated',
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        formStepStarted: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FormStepStarted,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: properties.formStepName,
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        formStepCompleted: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FormStepCompleted,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: properties.formStepName,
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        formCompleted: function (properties, options) {
            var _a;
            return sendEvent({
                name: EventNameEnum.FormCompleted,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: (_a = properties.formStepName) !== null && _a !== void 0 ? _a : 'completed',
                    submitStatus: properties.submitStatus,
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        formErred: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FormErred,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: properties.formStepName,
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        formCanceled: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FormCanceled,
                properties: {
                    formId: properties.formId,
                    formName: properties.formName,
                    formStepName: properties.formStepName,
                    formContext: properties.formContext,
                    formType: properties.formType,
                    transactionId: properties.transactionId,
                    formUserType: properties.formUserType,
                    formVersion: properties.formVersion,
                    dualSign: properties.dualSign,
                    appliedAmount: properties.appliedAmount,
                    approvedAmount: properties.approvedAmount,
                    formPurpose: properties.formPurpose,
                    formInitStatus: properties.formInitStatus,
                },
            }, getInitParams, options);
        },
        login: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.Login,
                properties: { loginStatus: properties.loginStatus },
            }, getInitParams, options);
        },
        search: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.Search,
                properties: {
                    searchResults: properties.searchResults,
                    searchTerm: properties.searchTerm.replace(/(\r\n|\n|\r)/gm, '').trim(),
                    searchType: properties.searchType,
                },
            }, getInitParams, options);
        },
        searchClicked: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.Search,
                properties: {
                    searchResults: properties.searchResults,
                    searchTerm: properties.searchTerm.replace(/(\r\n|\n|\r)/gm, '').trim(),
                    searchType: properties.searchType,
                    searchPosition: properties.searchPosition,
                },
            }, getInitParams, options);
        },
        internalPromotionClicked: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.InternalPromotionClicked,
                properties: {
                    internalPromotionName: properties.internalPromotionName,
                },
            }, getInitParams, options);
        },
        zipSearch: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.ZipSearch,
                properties: {
                    zipCode: properties.zipCode,
                    zipSearchResults: properties.zipSearchResults,
                    searchType: properties.searchType,
                },
            }, getInitParams, options);
        },
        zipSearchClick: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.ZipSearchClick,
                properties: {
                    zipCode: properties.zipCode,
                    zipSearchResults: properties.zipSearchResults,
                    searchType: properties.searchType,
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        zipSearchClickPrefilled: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.ZipSearchClickPrefilled,
                properties: {
                    searchType: properties.searchType,
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        findYourBankClick: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FindYourBankClick,
                properties: {
                    zipCode: properties.zipCode,
                    zipSearchResults: properties.zipSearchResults,
                    searchType: properties.searchType,
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        findYourBankClickPrefilled: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FindYourBankClickPrefilled,
                properties: {
                    searchType: properties.searchType,
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        calculatorInteraction: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.CalculatorInteraction,
                properties: {
                    calculatorName: properties.calculatorName,
                },
            }, getInitParams, options);
        },
        calculatorApplyForLoan: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.CalculatorApplyForLoan,
                properties: {
                    calculatorName: properties.calculatorName,
                },
            }, getInitParams, options);
        },
        calculatorContactUs: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.CalculatorContactUs,
                properties: {
                    calculatorName: properties.calculatorName,
                },
            }, getInitParams, options);
        },
        componentViewed: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.ComponentViewed,
                properties: {
                    componentName: properties.componentName,
                },
            }, getInitParams, options);
        },
        googleBankRedirect: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.GoogleBankRedirect,
                properties: {
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        autoBankRedirect: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.AutoBankRedirect,
                properties: {
                    bankName: properties.bankName,
                },
            }, getInitParams, options);
        },
        videoStart: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.VideoStart,
                properties: {
                    videoTitle: properties.videoTitle,
                },
            }, getInitParams, options);
        },
        videoEnd: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.VideoEnd,
                properties: {
                    videoTitle: properties.videoTitle,
                    videoPercentage: properties.videoPercentage,
                    videoTime: properties.videoTime,
                },
            }, getInitParams, options);
        },
        videoUpdate: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.VideoUpdate,
                properties: {
                    videoTitle: properties.videoTitle,
                    videoPercentage: properties.videoPercentage,
                    videoTime: properties.videoTime,
                },
            }, getInitParams, options);
        },
        discountPromoClicked: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.DiscountPromoClicked,
                properties: {
                    discountName: properties.discountName,
                },
            }, getInitParams, options);
        },
        discountOpenBox: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.DiscountOpenBox,
                properties: {
                    discountName: properties.discountName,
                },
            }, getInitParams, options);
        },
        discountExposed: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.DiscountExposed,
                properties: {
                    discountName: properties.discountName,
                },
            }, getInitParams, options);
        },
        faqRead: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FaqRead,
                properties: {
                    faqName: properties.faqName,
                },
            }, getInitParams, options);
        },
        feedbackGiven: function (properties, options) {
            return sendEvent({
                name: EventNameEnum.FeedbackGiven,
                properties: {
                    rating: properties.rating,
                    component: properties.component,
                },
            }, getInitParams, options);
        },
    };
};
//# sourceMappingURL=initWebBehaviorTracker.js.map